import { Slot } from "@radix-ui/react-slot";
import { PROJECTS_REGEX } from "files/hooks/useFiles";
import ProjectPrivilege from "rwb/pages/project/types/enums/privilege";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";
import useUserPermission from "~/hooks/useUserPermission";
import { FILE_BROWSER_API, PresignedUrlItem } from "~/lib/fileBrowserApi";
import useToast from "./toast";

interface FileDownloadProps {
  fileKey?: string;
  fileName?: string;
  onLoadingChange?: (loading: boolean) => void;
  children?: React.ReactNode;
}

const FileDownload = ({ fileKey = "", fileName, onLoadingChange, children }: FileDownloadProps) => {
  const { showError } = useToast();
  const downloadEnabled = useUserPermission("create:file-browser-download");
  const { data: projectAccessList } = useProjectsAccessList();

  const fileTitle = fileName ?? fileKey.split("/").at(-1) ?? "";
  const projectIdMatch = fileKey.match(PROJECTS_REGEX);

  if (!downloadEnabled) {
    return null;
  }

  if (projectIdMatch) {
    const projectId = projectIdMatch.groups?.projectId;
    const accessInfo = projectAccessList?.find(
      (project) => project.project_id === Number(projectId)
    );

    if (!accessInfo?.assigned_privileges.includes(ProjectPrivilege.DOWNLOAD_PROJECT_FILES)) {
      // Hide download CTA if it's a workbench file and current user doesn't have access to download
      return null;
    }
  }

  const downloadFile = async () => {
    const resp = await fetch(`${FILE_BROWSER_API}/download-urls`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keys: [fileKey] }),
    });

    if (resp.ok) {
      const [item]: PresignedUrlItem[] = await resp.json();
      const presignedDownloadUrl = item?.url;
      if (presignedDownloadUrl) {
        // Fetch as blob so anchor.download is respected — browsers ignore the
        // download attribute for cross-origin URLs (e.g. S3 presigned URLs).
        const fileResp = await fetch(presignedDownloadUrl);
        const blob = await fileResp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = blobUrl;
        anchor.style.display = "none";
        anchor.download = fileTitle;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
      }
    } else {
      showError("Unable to download file");
    }
  };

  const onClick = async () => {
    onLoadingChange?.(true);
    await downloadFile();
    onLoadingChange?.(false);
  };

  return (
    <>
      <Slot onClick={onClick}>{children}</Slot>
    </>
  );
};

export { FileDownload, type FileDownloadProps };
